import { useState, useEffect } from "react";
import { useNavigate, Outlet, useLocation } from "react-router-dom";
import {
  LayoutDashboard,
  KeyRound,
  Video,
  Users,
  ArrowLeft,
  Menu,
  BarChart3,
  FileText,
  MessageSquare,
  Upload,
  Headphones,
  Megaphone,
  Wifi,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";
import { useIsMobile } from "@/hooks/use-mobile";
import AdminLogin from "./AdminLogin";

const adminMenuItems = [
  { id: "dashboard", label: "控制台", icon: LayoutDashboard, path: "/admin" },
  { id: "analytics", label: "数据分析", icon: BarChart3, path: "/admin/analytics" },
  { id: "cdk", label: "卡密管理", icon: KeyRound, path: "/admin/cdk" },
  { id: "videos", label: "视频管理", icon: Video, path: "/admin/videos" },
  { id: "series", label: "系列管理", icon: LayoutDashboard, path: "/admin/series" },
  { id: "audios", label: "音频管理", icon: Headphones, path: "/admin/audios" },
  { id: "upload-logs", label: "上传日志", icon: Upload, path: "/admin/upload-logs" },
  { id: "users", label: "用户管理", icon: Users, path: "/admin/users" },
  { id: "feedback", label: "反馈管理", icon: MessageSquare, path: "/admin/feedback" },
  { id: "logs", label: "系统日志", icon: FileText, path: "/admin/logs" },
  { id: "announcements", label: "公告管理", icon: Megaphone, path: "/admin/announcements" },
  { id: "request-logs", label: "请求日志", icon: Wifi, path: "/admin/request-logs" },
];

export default function AdminLayout() {
  const navigate = useNavigate();
  const location = useLocation();
  const isMobile = useIsMobile();
  const { user, isLoading: authLoading } = useAuth();
  const [isAdmin, setIsAdmin] = useState(false);
  const [loading, setLoading] = useState(true);
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [mobileSidebarOpen, setMobileSidebarOpen] = useState(false);
  const [showLogin, setShowLogin] = useState(false);

  useEffect(() => {
    if (!isMobile) {
      setMobileSidebarOpen(false);
      return;
    }
    // Mobile keeps full-width sidebar content; collapse state is desktop-only.
    setSidebarOpen(true);
  }, [isMobile]);

  useEffect(() => {
    async function checkAdmin() {
      // Wait for auth to finish loading
      if (authLoading) {
        return;
      }

      // If no user, show admin login
      if (!user) {
        setShowLogin(true);
        setLoading(false);
        return;
      }

      // Check if user has admin role
      const { data } = await supabase.rpc("has_role", {
        _user_id: user.id,
        _role: "admin",
      });

      if (!data) {
        // User is logged in but not admin - show access denied
        setIsAdmin(false);
        setShowLogin(false);
        setLoading(false);
        return;
      }

      setIsAdmin(true);
      setShowLogin(false);
      setLoading(false);
    }

    checkAdmin();
  }, [user, authLoading]);

  const handleLoginSuccess = () => {
    // Re-trigger admin check after login
    setLoading(true);
    setShowLogin(false);
  };

  if (loading || authLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  // Show admin login if not authenticated
  if (showLogin) {
    return <AdminLogin onLoginSuccess={handleLoginSuccess} />;
  }

  // Show access denied if user is logged in but not admin
  if (!isAdmin) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-900 px-4">
        <div className="text-center">
          <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-red-500/20 flex items-center justify-center">
            <span className="text-3xl">⚠️</span>
          </div>
          <h1 className="text-xl font-bold text-white mb-2">访问被拒绝</h1>
          <p className="text-slate-400 mb-6">当前账号不是管理员，无法访问后台。</p>
          <Button onClick={() => navigate("/")} variant="outline">
            返回首页
          </Button>
        </div>
      </div>
    );
  }

  const handleMenuNavigate = (path: string) => {
    navigate(path);
    if (isMobile) setMobileSidebarOpen(false);
  };

  return (
    <div className="min-h-screen bg-background">
      {isMobile && mobileSidebarOpen && (
        <button
          type="button"
          aria-label="关闭侧边栏"
          className="fixed inset-0 z-40 bg-black/45"
          onClick={() => setMobileSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside
        className={cn(
          "fixed left-0 top-0 z-50 h-[100dvh] bg-sidebar border-r border-sidebar-border transition-all duration-300",
          isMobile
            ? [
                "w-[82vw] max-w-[320px] shadow-xl",
                mobileSidebarOpen ? "translate-x-0" : "-translate-x-full",
              ]
            : [sidebarOpen ? "w-56" : "w-16", "translate-x-0"],
        )}
      >
        <div className="flex h-full flex-col">
          {/* Logo */}
          <div className="flex h-14 items-center justify-between px-4 border-b border-sidebar-border">
            {(sidebarOpen || isMobile) && (
              <div className="flex items-center gap-2">
                <span className="text-lg">🍡</span>
                <span className="font-bold text-sidebar-foreground">DangoJP</span>
              </div>
            )}
            <Button
              type="button"
              variant="ghost"
              size="icon"
              onClick={() => {
                if (isMobile) {
                  setMobileSidebarOpen(false);
                  return;
                }
                setSidebarOpen((v) => !v);
              }}
              className="h-8 w-8 text-sidebar-foreground"
            >
              <Menu className="h-4 w-4" />
            </Button>
          </div>

          {/* Navigation */}
          <nav className="flex-1 py-4 px-2 space-y-1 overflow-y-auto">
            {adminMenuItems.map((item) => {
              const Icon = item.icon;
              const isActive = location.pathname === item.path;

              return (
                <Button
                  key={item.id}
                  type="button"
                  variant="ghost"
                  className={cn(
                    "w-full justify-start gap-3 h-11",
                    !isMobile && !sidebarOpen && "justify-center px-2",
                    isActive
                      ? "bg-sidebar-accent text-sidebar-primary font-medium"
                      : "text-sidebar-foreground/70 hover:text-sidebar-foreground",
                  )}
                  onClick={() => handleMenuNavigate(item.path)}
                >
                  <Icon className="h-5 w-5 shrink-0" />
                  {(sidebarOpen || isMobile) && <span className="truncate">{item.label}</span>}
                </Button>
              );
            })}
          </nav>

          {/* Back to site */}
          <div className="border-t border-sidebar-border p-2">
            <Button
              type="button"
              variant="ghost"
              className={cn(
                "w-full justify-start gap-3 h-11 text-sidebar-foreground/60",
                !isMobile && !sidebarOpen && "justify-center px-2",
              )}
              onClick={() => {
                navigate("/");
                if (isMobile) setMobileSidebarOpen(false);
              }}
            >
              <ArrowLeft className="h-5 w-5 shrink-0" />
              {(sidebarOpen || isMobile) && <span>返回前台</span>}
            </Button>
          </div>
        </div>
      </aside>

      {/* Main content */}
      <main
        className={cn(
          "transition-all duration-300 min-w-0",
          isMobile ? "ml-0" : sidebarOpen ? "ml-56" : "ml-16",
        )}
      >
        {isMobile && (
          <header className="sticky top-0 z-30 flex h-12 items-center gap-2 border-b bg-background/95 px-3 backdrop-blur">
            <Button
              type="button"
              variant="ghost"
              size="icon"
              className="h-8 w-8"
              onClick={() => setMobileSidebarOpen(true)}
            >
              <Menu className="h-4 w-4" />
            </Button>
            <span className="text-sm font-semibold text-foreground">管理后台</span>
          </header>
        )}

        <div className="min-w-0 overflow-x-auto">
          <Outlet />
        </div>
      </main>
    </div>
  );
}
